"use client";

/**
 * Phase 52 — client-side shell that dynamically imports the
 * non-critical marketing widgets (ChatWidget) with
 * `ssr: false` so their code stays out of the initial server HTML
 * payload.
 *
 * Next.js 16 disallows `ssr: false` dynamic imports in server components,
 * so this wrapper exists solely to host those imports from a client
 * boundary while keeping the parent marketing layout a server component.
 *
 * Every widget renders `null` until an interaction or consent
 * event fires — no placeholder/loading UI is intentionally shown.
 *
 * INP (draft 8 §2.2 A3, row 0): none of these widgets is needed during
 * hydration, so their chunks are requested only once the main thread is idle,
 * and the chat chunk only once the visitor has made a complete cookie choice
 * (the widget renders nothing before that anyway).
 */

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { subscribeToBrowserConsent } from "@/components/analytics/browser-consent-lifecycle";
import { scheduleIdleTask } from "@/lib/analytics/main-thread";

const ChatWidget = dynamic(() => import("@/components/chat/chat-widget"), {
  ssr: false,
});

// sonner keeps every toast fired before a Toaster subscribes and replays the
// active ones on mount (sonner 2 `Observer.subscribe`), so a toast raised by a
// lead form or the chat escalation before this idle mount is not lost.
const Toaster = dynamic(
  () => import("sonner").then((m) => ({ default: m.Toaster })),
  { ssr: false },
);

// Consent-gated exactly like the app shell's capture: it registers the
// web-vitals observers only after the PostHog SDK has loaded with accepted
// Analytics consent. LCP and CLS are fully buffered, but INP is not: the
// browser buffers only Event Timing entries of 104 ms or more, so an
// interaction before this idle mount is reported only when it was that slow
// (the ones that matter for INP), and lighter early interactions are missed.
// An early interaction after a client-side navigation is attributed to an
// unknown route and dropped (lib/analytics/web-vitals-attribution.ts).
const WebVitalsCapture = dynamic(
  () =>
    import("@/components/analytics/web-vitals").then((m) => ({
      default: m.WebVitalsCapture,
    })),
  { ssr: false },
);

const UtmCapture = dynamic(
  () =>
    import("@/components/analytics/utm-capture").then((m) => ({
      default: m.UtmCapture,
    })),
  { ssr: false },
);

const WIDGET_IDLE_TIMEOUT_MS = 3_000;

/** True once the main thread has gone idle after mount (bounded). */
function useIdleReady(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(
    () =>
      scheduleIdleTask(() => setReady(true), {
        timeout: WIDGET_IDLE_TIMEOUT_MS,
      }),
    [],
  );
  return ready;
}

/**
 * True once the visitor has a complete cookie choice AND the main thread has
 * idled after it. Latches: once the chat chunk is mounted the widget itself
 * hides again if the choice later becomes incomplete (re-prompt or expiry).
 */
function useChatWidgetReady(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready) return;
    let cancelIdle: (() => void) | null = null;
    const unsubscribe = subscribeToBrowserConsent(({ snapshot }) => {
      if (!snapshot.complete) {
        cancelIdle?.();
        cancelIdle = null;
        return;
      }
      cancelIdle ??= scheduleIdleTask(() => setReady(true), {
        timeout: WIDGET_IDLE_TIMEOUT_MS,
      });
    });
    return () => {
      cancelIdle?.();
      unsubscribe();
    };
  }, [ready]);
  return ready;
}

export function MarketingDeferredWidgets() {
  const idleReady = useIdleReady();
  const chatReady = useChatWidgetReady();
  return (
    <>
      {chatReady ? <ChatWidget context="marketing" enableEscalation /> : null}
      {/* Logged-out escalation surfaces sonner toasts on submit; the marketing
          layout has no Toaster of its own, so mount one here (off the hydration
          critical path with the rest of the deferred widgets). */}
      {idleReady ? (
        <Toaster
          position="bottom-center"
          toastOptions={{ className: "font-sans" }}
          richColors
        />
      ) : null}
      {idleReady ? <WebVitalsCapture /> : null}
    </>
  );
}

// UtmCapture is a separate wrapper because the marketing layout mounts it
// inside the PostHogProvider tree (not after it). UtmCapture returns null and
// reads URL params + writes localStorage in useEffect — safe to defer past the
// hydration critical path. Removes posthog-js as an eager transitive import
// from the marketing route trees.
export function MarketingUtmCapture() {
  return <UtmCapture />;
}
